// Phase 21 · Create room form
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mic, Video, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["marketing", "seo", "sales", "growth", "ai-tools", "automation", "analytics", "design", "product", "ops", "hr", "finance", "legal", "customer-success"];

export default function CreateRoomPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [form, setForm] = useState({
        room_type: "audio",
        title: "",
        description: "",
        category_slug: "marketing",
        max_participants: "",
        rules: "",
        visibility: "public",
        org_id: "",
    });
    const [busy, setBusy] = useState(false);
    const [flags, setFlags] = useState({});

    useEffect(() => {
        fetch(`${API}/api/community/flags`, { credentials: "include" })
            .then((r) => r.ok ? r.json() : {})
            .then(setFlags).catch(() => {});
    }, []);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.title.trim() || form.title.trim().length < 3) return toast.error("Title 3–100 chars");
        if (form.visibility === "corporate" && !form.org_id.trim()) return toast.error("org_id required for corporate visibility");
        setBusy(true);
        try {
            const payload = { ...form };
            if (form.max_participants) payload.max_participants = Number(form.max_participants);
            else delete payload.max_participants;
            if (form.visibility !== "corporate") delete payload.org_id;
            const r = await fetch(`${API}/api/community/rooms`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(payload),
            });
            const data = await r.json();
            if (!r.ok) {
                const err = data?.detail?.error || data?.detail?.[0]?.msg || "Create failed";
                toast.error(err);
                return;
            }
            toast.success("Room created — click Go live when ready");
            const seg = data.room_type === "audio" ? "audio" : "video";
            navigate(`/rooms/${seg}/${data.slug || data.id}`);
        } catch { toast.error("Network error"); }
        setBusy(false);
    };

    const showPrivate = !!flags?.COMMUNITY_PRIVATE_ENABLED;
    const showCorporate = !!flags?.COMMUNITY_CORPORATE_ENABLED;
    const anyVisibilityOn = showPrivate || showCorporate;

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="create-room-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/rooms" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Rooms
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold">Create room</h1>
                </div>
            </header>
            <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
                <section>
                    <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Room type</label>
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={() => setField("room_type", "audio")}
                            className={`p-4 rounded-xl border transition-all text-left ${form.room_type === "audio" ? "border-[#FFFFFF] bg-[#FFFFFF]/10" : "border-[#1A1A1A] hover:border-[#FFFFFF]/30"}`}
                            data-testid="create-type-audio"
                        >
                            <Mic size={18} className="text-[#FFFFFF] mb-1" />
                            <p className="text-sm font-semibold text-neutral-100">Audio</p>
                            <p className="text-[10px] text-neutral-500 mt-1">Twitter Spaces / Clubhouse style · up to 100 listeners + 8 speakers</p>
                        </button>
                        <button
                            type="button"
                            onClick={() => setField("room_type", "video")}
                            className={`p-4 rounded-xl border transition-all text-left ${form.room_type === "video" ? "border-[#FAFAFA] bg-[#FAFAFA]/10" : "border-[#1A1A1A] hover:border-[#FAFAFA]/30"}`}
                            data-testid="create-type-video"
                        >
                            <Video size={18} className="text-[#FAFAFA] mb-1" />
                            <p className="text-sm font-semibold text-neutral-100">Video</p>
                            <p className="text-[10px] text-neutral-500 mt-1">Zoom-style grid · up to 25 total participants</p>
                        </button>
                    </div>
                </section>

                <section>
                    <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Title</label>
                    <input
                        type="text"
                        value={form.title}
                        onChange={(e) => setField("title", e.target.value)}
                        maxLength={100}
                        placeholder="Fireside chat: cold outreach ROI"
                        className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-4 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                        data-testid="create-title-input"
                    />
                </section>

                <section>
                    <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Description</label>
                    <textarea
                        value={form.description}
                        onChange={(e) => setField("description", e.target.value)}
                        maxLength={2000}
                        rows={3}
                        placeholder="What are you discussing? Format, agenda, expected duration."
                        className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-4 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                        data-testid="create-desc-input"
                    />
                </section>

                <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Category</label>
                        <select
                            value={form.category_slug}
                            onChange={(e) => setField("category_slug", e.target.value)}
                            className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="create-category"
                        >
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">
                            Max participants <span className="text-[10px] normal-case text-neutral-500">(default {form.room_type === "audio" ? 100 : 25})</span>
                        </label>
                        <input
                            type="number"
                            min={2}
                            max={form.room_type === "audio" ? 500 : 25}
                            value={form.max_participants}
                            onChange={(e) => setField("max_participants", e.target.value)}
                            placeholder={form.room_type === "audio" ? "100" : "25"}
                            className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="create-max-participants"
                        />
                    </div>
                </section>

                <section>
                    <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Ground rules (optional)</label>
                    <textarea
                        value={form.rules}
                        onChange={(e) => setField("rules", e.target.value)}
                        maxLength={2000}
                        rows={2}
                        placeholder="Be respectful, on-topic, no self-promo."
                        className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-4 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                        data-testid="create-rules-input"
                    />
                </section>

                {anyVisibilityOn && (
                    <section>
                        <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1 inline-flex items-center gap-1">
                            <Lock size={11} /> Visibility
                        </label>
                        <select
                            value={form.visibility}
                            onChange={(e) => setField("visibility", e.target.value)}
                            className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="create-visibility-input"
                        >
                            <option value="public">Public — discoverable + open join</option>
                            {showPrivate && <option value="private">Private — invite / approval only</option>}
                            {showCorporate && <option value="corporate">Corporate — organization members only</option>}
                        </select>
                        {form.visibility === "corporate" && (
                            <input
                                type="text"
                                value={form.org_id}
                                onChange={(e) => setField("org_id", e.target.value)}
                                placeholder="Organization ID (community_orgs.id)"
                                className="mt-2 w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-4 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                                data-testid="create-org-id-input"
                            />
                        )}
                    </section>
                )}

                <div className="flex items-center justify-between pt-2">
                    <p className="text-[10px] text-neutral-500 flex items-center gap-1">
                        <Sparkles size={11} /> Public V4 · 5 rooms/day · UI-only placeholder (no WebRTC) · signed in as @{user?.username || user?.email}
                    </p>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-5 py-2 bg-white text-neutral-900 font-semibold rounded-xl text-sm hover:opacity-90 disabled:opacity-40"
                        data-testid="create-submit-btn"
                    >
                        {busy ? "Creating…" : "Create room"}
                    </button>
                </div>
            </main>
        </div>
    );
}
